import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { Overline, Reveal, scrollToId } from "./Reveal";
import { IMAGES } from "@/lib/images";

const SERVICES = [
  {
    n: "01",
    title: "Interior Precision",
    img: IMAGES.interior,
    desc: "Walls, ceilings, trim and feature walls finished with zero-tolerance prep. Dust-controlled sanding, hand-cut lines and premium low-VOC coatings for living spaces that feel gallery-grade.",
    points: ["Feature walls & colour drenching", "Heritage cornice & trim work", "Low-VOC family-safe coatings"],
  },
  {
    n: "02",
    title: "Exterior Cladding",
    img: IMAGES.cladding,
    desc: "House exteriors done properly — Hebel panels, timber weatherboards and composite cladding cleaned, sealed and coated with weather-armoured systems built for South Australian sun and storms.",
    points: ["Hebel panel painting & sealing", "Wooden cladding & weatherboards", "Fibre cement & composite panels"],
  },
  {
    n: "03",
    title: "Commercial & Heritage",
    img: IMAGES.commercial,
    desc: "Off-hours crews, brand-matched palettes and staged handovers that keep your business running. Heritage-listed restorations delivered to council and trust specifications.",
    points: ["Night & weekend programs", "Heritage council compliance", "Brand colour matching"],
  },
  {
    n: "04",
    title: "Cabinet Refinishing",
    img: IMAGES.cabinet,
    desc: "Factory-grade sprayed finishes from our Lonsdale spray booth. Kitchens, wardrobes and joinery returned looking newly built — at a fraction of replacement cost.",
    points: ["HVLP sprayed 2-pac finishes", "Door-off, booth-cured process", "Hardware upgrade options"],
  },
  {
    n: "05",
    title: "Woodwork & Joinery",
    img: IMAGES.woodwork,
    desc: "Interior timber finished to a furniture standard. Wood cladding and panelled feature walls, enamel-finished wooden doors, plus windows and architraves — sanded, primed and hand-finished for a glass-smooth result.",
    points: ["Wood cladding & feature panelling", "Enamel paint on wooden doors", "Windows & architraves"],
  },
];

export default function ServicesSection() {
  const [open, setOpen] = useState(0);

  return (
    <section id="services" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32" data-testid="services-section">
      <Reveal>
        <Overline>What we do</Overline>
        <h2 className="mt-4 max-w-2xl font-serif text-4xl font-bold tracking-tight sm:text-5xl">
          SERVICES<span className="text-accent">.</span>
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          Five disciplines, one obsession: the finish. Every service includes
          substrate diagnostics, obsessive preparation and a daylight sign-off.
        </p>
      </Reveal>

      <div className="mt-14 border-t border-border">
        {SERVICES.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <div
              className={`border-b border-border ${open === i ? "bg-card" : ""}`}
              data-testid={`service-card-${s.n}`}
            >
              <button
                className="flex w-full items-center gap-5 px-2 py-6 text-left sm:gap-10 sm:px-6"
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                data-testid={`service-toggle-${s.n}`}
              >
                <span className="font-mono text-sm font-bold text-accent">{s.n}</span>
                <span className="flex-1 font-serif text-2xl font-semibold tracking-tight sm:text-3xl">
                  {s.title}
                </span>
                <Plus
                  className={`h-5 w-5 shrink-0 transition-transform duration-300 ${
                    open === i ? "rotate-45 text-accent" : "text-muted-foreground"
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="grid gap-8 px-2 pb-8 sm:grid-cols-2 sm:px-6 sm:pl-[4.5rem]">
                      <div>
                        <p className="max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                          {s.desc}
                        </p>
                        <ul className="mt-5 space-y-2">
                          {s.points.map((p) => (
                            <li key={p} className="flex items-center gap-3 text-sm">
                              <span className="h-1 w-1 rounded-full bg-accent" />
                              {p}
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={() => scrollToId("contact")}
                          data-testid={`service-quote-${s.n}`}
                          className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-accent"
                        >
                          Request this service
                          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                      </div>
                      <div
                        className="relative aspect-[16/10] overflow-hidden"
                        style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}
                      >
                        <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
